import React from 'react';

import { Box, Grid, Paper, Typography } from '@mui/material';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import {
  formatAmountWithAud,
  formatDate,
  formatStayPrice,
  lowestStayOf,
  plural,
  stayBasis,
  taxBasisLabel,
} from './utils';

interface Props {
  property: CompetitorProperty;
}

interface FactProps {
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
}

function Fact({ label, value, note }: FactProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="medium">
        {value}
      </Typography>
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </Box>
  );
}

export default function CompetitorOfferSummary({ property }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {property.location && (
        <Typography variant="body2" color="text.secondary">
          {property.location}
        </Typography>
      )}

      <Grid container spacing={3} mt={0.5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact
            label="Lead price"
            value={formatAmountWithAud(property.leadPrice, property.leadCurrency, property.leadPriceAud)}
            note={[taxBasisLabel(property.leadPriceIncludesTax), property.leadPriceBasis?.replace(/_/g, ' ')]
              .filter(Boolean)
              .join(' · ')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact
            label="Was price"
            value={formatAmountWithAud(property.wasPrice, property.leadCurrency, property.wasPriceAud)}
            note={property.discountPct !== undefined ? `${property.discountPct}% off` : 'No discount published'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact
            label="Lowest stay"
            value={formatStayPrice(lowestStayOf(property))}
            note={stayBasis(property.lowestStayNights, property.lowestStayAdults, property.lowestStayCheckIn)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact
            label="Sale dates (as published)"
            value={`${formatDate(property.saleStart)} – ${formatDate(property.saleEnd)}`}
            note="Secret Escapes mostly publishes far-future placeholder end dates"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact
            label="Collected"
            value={`${formatDate(property.firstCollectedAt)} – ${formatDate(property.lastCollectedAt)}`}
            note={
              property.collectionCount !== undefined
                ? `Found by ${plural(property.collectionCount, 'collection')}`
                : undefined
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Fact label="Room options" value={plural(property.optionCount, 'option')} />
        </Grid>
      </Grid>
    </Paper>
  );
}
